import { ClipboardEvent, FC, useEffect, useMemo, useState } from 'react'
import CN from 'classnames'
import {
  NotificationProcessCatalogueEntry,
  useCreateEmailAlertGroup,
  useGetEmailAlertCardsList,
  useGetNotificationProcessCatalogue,
  useGetProcessOptions,
  useGetTagOptions,
} from 'framework/api/methods'

import { Button } from 'components/atoms/Button'
import { Drawer } from 'components/atoms/Drawer'
import { Input } from 'components/atoms/Input'
import { SimpleSelect } from 'components/atoms/Select'
import { isEmail } from 'utils'

import { NotificationRecipientCard } from '../../pages/configurations/EmailNotificationRecipients/EmailAlertsCardList/EmailAlertReportCard'
import {
  filterPartnerOption,
  formatPartnerOptionLabel,
  PartnerFilterOption,
  usePartnerOptions,
} from '../../pages/configurations/EmailNotificationRecipients/EmailAlertsFilterBar/PartnerFilterDropdown'
import {
  buildCatalogueProcesses,
  CatalogueOutcome,
  CatalogueProcess,
  describeOutcome,
  EmailAlertGroupPrefill,
  OUTCOME_FAILURE,
  OUTCOME_SUCCESS,
  serviceNameForOutcome,
} from '../../pages/configurations/EmailNotificationRecipients/processCatalogue'

export interface AddEmailAlertGroupProps {
  className?: string | undefined
  isActive: boolean
  onCloseClick: () => void
  notify: (message: string, type: 'success' | 'error') => void
  onCreated: () => void
  /** Pre-selects partner / process / outcome when opened from a "Not configured — Set up" affordance (BDL20-4262). */
  prefill?: EmailAlertGroupPrefill | null
  /** Opens the recipients modal for a group that already exists instead of creating a duplicate. */
  onManageExisting?: (card: NotificationRecipientCard) => void
}

type OutcomeChoice = CatalogueOutcome | 'Both'

/** Sentinel for the "Other (advanced)…" pick-list entry that reveals the free-text Service Name. */
const OTHER_PROCESS_VALUE = '__other__'

const OUTCOME_CHOICES: { value: OutcomeChoice; label: string }[] = [
  { value: OUTCOME_FAILURE, label: 'Failure' },
  { value: OUTCOME_SUCCESS, label: 'Success' },
  { value: 'Both', label: 'Both' },
]

/** Groups are keyed by ServiceName and the senders match it exactly — never more than the catalogue offers. */
const EXISTING_CARDS_TAKE = 1000

interface ProcessSelectOption {
  value: string
  label: string
  catalogueProcess: CatalogueProcess | null
}

interface GroupForm {
  partner: PartnerFilterOption | null
  processValue: string
  outcome: OutcomeChoice | ''
  serviceName: string
  serviceNameDescription: string
  tag: string
  recipientEmails: string[]
}

interface GroupTarget {
  serviceName: string
  serviceNameDescription: string
}

const initialForm = (prefill?: EmailAlertGroupPrefill | null): GroupForm => ({
  partner: prefill
    ? {
        id: prefill.partnerId,
        value: prefill.partnerId,
        label: prefill.partnerName,
        code: prefill.partnerCode,
      }
    : null,
  processValue: prefill?.process ?? '',
  outcome: prefill?.outcome ?? '',
  serviceName: '',
  serviceNameDescription: '',
  tag: '',
  recipientEmails: [''],
})

/** Splits a pasted list on comma, semicolon, whitespace or newline; trims, drops blanks, de-duplicates case-insensitively. */
export const splitEmailList = (text: string): string[] => {
  const seen = new Set<string>()
  return text
    .split(/[,;\s]+/)
    .map((part) => part.trim())
    .filter((part) => {
      if (!part) return false
      const key = part.toLowerCase()
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
}

const apiErrorMessage = (error: unknown, fallback: string): string => {
  const data = (error as { response?: { data?: { message?: string } } })
    ?.response?.data
  return data?.message || fallback
}

const formatProcessOptionLabel = (option: ProcessSelectOption) => (
  <div className='flex items-center gap-2'>
    <span
      className={CN('truncate', {
        'font-semibold text-N-800': option.catalogueProcess !== null,
        'text-N-600 italic': option.catalogueProcess === null,
      })}>
      {option.label}
    </span>
    {option.catalogueProcess?.hasOutcomes && (
      <span className='text-[11px] font-semibold text-N-500 bg-N-100 rounded px-2 py-[1px]'>
        Success / Failure
      </span>
    )}
  </div>
)

export const AddEmailAlertGroup: FC<AddEmailAlertGroupProps> = ({
  className,
  isActive,
  onCloseClick,
  notify,
  onCreated,
  prefill,
  onManageExisting,
}: AddEmailAlertGroupProps) => {
  const { isLoading: partnersLoading, options: partnerOptions } =
    usePartnerOptions()
  const { data: processOptionsData } = useGetProcessOptions()
  const { data: tagOptionsData } = useGetTagOptions()
  const {
    data: catalogueData,
    isError: catalogueFailed,
    isLoading: catalogueLoading,
  } = useGetNotificationProcessCatalogue()

  const descriptionOptions: string[] = processOptionsData?.processOptions ?? []
  const tagOptions: string[] = tagOptionsData?.tagOptions ?? []
  const catalogueEntries: NotificationProcessCatalogueEntry[] =
    catalogueData?.processes ?? []
  // No catalogue (request failed or empty) → the drawer falls back to the free-text Service Name.
  const catalogueAvailable = !catalogueFailed && catalogueEntries.length > 0

  const [form, setForm] = useState<GroupForm>(() => initialForm(prefill))
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [conflict, setConflict] = useState<NotificationRecipientCard | null>(
    null,
  )
  const [saving, setSaving] = useState<'save' | 'another' | null>(null)

  const { mutateAsync: createGroup, isLoading: isCreating } =
    useCreateEmailAlertGroup()

  // Groups the selected partner already has — the duplicate check runs against these before any POST.
  const { data: existingCardsData } = useGetEmailAlertCardsList(
    {
      partnerId: form.partner?.id,
      take: EXISTING_CARDS_TAKE,
      skip: 0,
    },
    { enabled: !!form.partner },
  )
  const existingCards: NotificationRecipientCard[] =
    existingCardsData?.cards ?? []

  const catalogueProcesses = useMemo(
    () => buildCatalogueProcesses(catalogueEntries, form.partner?.code),
    [catalogueEntries, form.partner?.code],
  )

  const processSelectOptions = useMemo<ProcessSelectOption[]>(
    () => [
      ...catalogueProcesses.map((process) => ({
        value: process.process,
        label: process.displayName,
        catalogueProcess: process,
      })),
      {
        value: OTHER_PROCESS_VALUE,
        label: 'Other (advanced)…',
        catalogueProcess: null,
      },
    ],
    [catalogueProcesses],
  )

  const selectedProcessOption =
    processSelectOptions.find((option) => option.value === form.processValue) ??
    null
  const selectedCatalogueProcess =
    selectedProcessOption?.catalogueProcess ?? null
  const isOther =
    !catalogueAvailable || form.processValue === OTHER_PROCESS_VALUE
  const showOutcome = !isOther && selectedCatalogueProcess?.hasOutcomes === true

  // Picking a process (or outcome) fills description + tag from the catalogue; both stay editable.
  useEffect(() => {
    if (!selectedCatalogueProcess) return
    const outcome: CatalogueOutcome | null =
      form.outcome === OUTCOME_SUCCESS || form.outcome === OUTCOME_FAILURE
        ? form.outcome
        : null
    setForm((prev) => ({
      ...prev,
      serviceNameDescription: describeOutcome(
        selectedCatalogueProcess.displayName,
        outcome,
      ),
      tag: selectedCatalogueProcess.defaultTag,
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedCatalogueProcess?.process,
    selectedCatalogueProcess?.displayName,
    selectedCatalogueProcess?.defaultTag,
    form.outcome,
  ])

  const buildTargets = (): GroupTarget[] => {
    const description = form.serviceNameDescription.trim()
    if (isOther || !selectedCatalogueProcess) {
      return [
        {
          serviceName: form.serviceName.trim(),
          serviceNameDescription: description,
        },
      ]
    }
    const { process } = selectedCatalogueProcess
    if (!selectedCatalogueProcess.hasOutcomes) {
      return [{ serviceName: process, serviceNameDescription: description }]
    }
    if (form.outcome === 'Both') {
      const outcomes: CatalogueOutcome[] = [OUTCOME_SUCCESS, OUTCOME_FAILURE]
      return outcomes.map((outcome) => ({
        serviceName: serviceNameForOutcome(process, outcome),
        serviceNameDescription: describeOutcome(description, outcome),
      }))
    }
    const outcome: CatalogueOutcome =
      form.outcome === OUTCOME_SUCCESS ? OUTCOME_SUCCESS : OUTCOME_FAILURE
    return [
      {
        serviceName: serviceNameForOutcome(process, outcome),
        serviceNameDescription: description,
      },
    ]
  }

  const targetServiceNames = (): string[] => {
    if (isOther) return form.serviceName.trim() ? [form.serviceName.trim()] : []
    if (!selectedCatalogueProcess) return []
    if (!selectedCatalogueProcess.hasOutcomes)
      return [selectedCatalogueProcess.process]
    if (!form.outcome) return []
    return buildTargets().map((target) => target.serviceName)
  }

  const resetAndClose = () => {
    setForm(initialForm(null))
    setErrors({})
    setConflict(null)
    onCloseClick()
  }

  const clearFieldError = (field: string) =>
    setErrors((prev) => (prev[field] ? { ...prev, [field]: '' } : prev))

  const handlePartnerChange = (option: PartnerFilterOption | null) => {
    setConflict(null)
    clearFieldError('partner')
    setForm((prev) => {
      const stillApplies =
        prev.processValue === '' ||
        prev.processValue === OTHER_PROCESS_VALUE ||
        buildCatalogueProcesses(catalogueEntries, option?.code).some(
          (process) => process.process === prev.processValue,
        )
      return {
        ...prev,
        partner: option,
        processValue: stillApplies ? prev.processValue : '',
        outcome: stillApplies ? prev.outcome : '',
      }
    })
  }

  const handleProcessChange = (option: ProcessSelectOption | null) => {
    setConflict(null)
    clearFieldError('process')
    clearFieldError('outcome')
    setForm((prev) => ({
      ...prev,
      processValue: option?.value ?? '',
      outcome: '',
      serviceName: '',
      ...(option?.value === OTHER_PROCESS_VALUE
        ? { serviceNameDescription: '', tag: '' }
        : {}),
    }))
  }

  const handleOutcomeChange = (outcome: OutcomeChoice) => {
    setConflict(null)
    clearFieldError('outcome')
    setForm((prev) => ({ ...prev, outcome }))
  }

  const updateRecipientEmail = (index: number, value: string) => {
    setForm((prev) => ({
      ...prev,
      recipientEmails: prev.recipientEmails.map((email, i) =>
        i === index ? value : email,
      ),
    }))
  }

  const addRecipientRow = () => {
    setForm((prev) => ({
      ...prev,
      recipientEmails: [...prev.recipientEmails, ''],
    }))
  }

  const removeRecipientRow = (index: number) => {
    setForm((prev) => ({
      ...prev,
      recipientEmails: prev.recipientEmails.filter((_, i) => i !== index),
    }))
  }

  // Pasting a list (comma / semicolon / whitespace / newline separated) becomes one row per address.
  const handleRecipientPaste = (
    index: number,
    event: ClipboardEvent<HTMLInputElement>,
  ) => {
    const pasted = splitEmailList(event.clipboardData.getData('text'))
    if (pasted.length <= 1) return // a single address pastes into the field as usual
    event.preventDefault()
    clearFieldError('recipientEmails')
    setForm((prev) => {
      // Drop the (blank) row pasted into; keep anything already typed.
      const kept = prev.recipientEmails.filter(
        (email, i) => i !== index || email.trim().length > 0,
      )
      const seen = new Set(
        kept.map((email) => email.trim().toLowerCase()).filter(Boolean),
      )
      const additions = pasted.filter((email) => {
        const key = email.toLowerCase()
        if (seen.has(key)) return false
        seen.add(key)
        return true
      })
      const next = [...kept, ...additions]
      return { ...prev, recipientEmails: next.length > 0 ? next : [''] }
    })
  }

  const validate = (): boolean => {
    const nextErrors: Record<string, string> = {}

    if (!form.partner) nextErrors.partner = 'Select a partner'
    if (isOther) {
      if (!form.serviceName.trim())
        nextErrors.serviceName = 'Service name is required'
    } else if (!selectedCatalogueProcess) {
      nextErrors.process = 'Select a process'
    } else if (selectedCatalogueProcess.hasOutcomes && !form.outcome) {
      nextErrors.outcome = 'Choose Failure, Success or Both'
    }
    if (!form.serviceNameDescription.trim())
      nextErrors.serviceNameDescription = 'Process/description is required'
    if (!form.tag.trim()) nextErrors.tag = 'Tag is required'

    const trimmedEmails = form.recipientEmails
      .map((email) => email.trim())
      .filter((email) => email.length > 0)

    if (trimmedEmails.length === 0) {
      nextErrors.recipientEmails = 'At least one recipient email is required'
    } else {
      const invalid = trimmedEmails.find((email) => !isEmail(email))
      if (invalid) {
        nextErrors.recipientEmails = `"${invalid}" is not a valid email address`
      } else {
        const lower = trimmedEmails.map((e) => e.toLowerCase())
        if (new Set(lower).size !== lower.length) {
          nextErrors.recipientEmails = 'Duplicate recipient emails entered'
        }
      }
    }

    setErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  const handleSave = async (addAnother: boolean) => {
    setConflict(null)
    if (!validate()) return
    const { partner } = form
    if (!partner) return

    const targets = buildTargets()

    // Already exists for this partner? Point at Manage recipients instead of a 400 from the API.
    const existing = targets
      .map((target) =>
        existingCards.find(
          (card) =>
            card.partnerId === partner.id &&
            card.serviceName.trim().toUpperCase() ===
              target.serviceName.toUpperCase(),
        ),
      )
      .find((card): card is NotificationRecipientCard => card !== undefined)
    if (existing) {
      setConflict(existing)
      return
    }

    const recipientEmails = form.recipientEmails
      .map((email) => email.trim())
      .filter((email) => email.length > 0)
    const tag = form.tag.trim()

    setSaving(addAnother ? 'another' : 'save')
    const created: string[] = []
    let failed = false
    for (const target of targets) {
      try {
        await createGroup({
          partnerId: partner.id,
          serviceName: target.serviceName,
          serviceNameDescription: target.serviceNameDescription,
          tag,
          recipientEmails,
        })
        created.push(target.serviceName)
      } catch (error) {
        failed = true
        notify(
          `Could not create ${target.serviceName}: ${apiErrorMessage(
            error,
            'request failed',
          )}`,
          'error',
        )
        break
      }
    }
    setSaving(null)

    if (created.length === 0) return
    notify(`Created ${created.join(' and ')} for ${partner.label}`, 'success')
    onCreated()
    if (failed) return // keep the form so the remaining group can be retried
    if (addAnother) {
      setForm({ ...initialForm(null), partner })
      setErrors({})
      setConflict(null)
    } else {
      resetAndClose()
    }
  }

  const serviceNamesPreview = targetServiceNames()
  const invalidEmailRows = errors.recipientEmails
    ? form.recipientEmails.map(
        (email) => email.trim().length > 0 && !isEmail(email.trim()),
      )
    : []

  return (
    <Drawer
      isActive={isActive}
      onCloseClick={resetAndClose}
      width={520}
      title='New Email Group'>
      <div
        className={CN(
          'flex flex-col py-4 px-5 relative h-full overflow-y-auto overscroll-y-auto styled-scroll gap-y-5',
          className,
        )}>
        <div className='flex flex-col w-full'>
          <span className='pb-2 text-N-700'>
            Partner<span className='text-R-500 ml-[2px]'>*</span>
          </span>
          <SimpleSelect
            isLoading={partnersLoading}
            placeholder='Select partner…'
            value={form.partner}
            onChange={handlePartnerChange}
            options={partnerOptions}
            isError={!!errors.partner}
            // SimpleSelect only forwards the `restProps` object to react-select (BDL20-4262).
            restProps={{
              formatOptionLabel: formatPartnerOptionLabel,
              filterOption: filterPartnerOption,
            }}
          />
          {errors.partner && (
            <span className='text-R-500 text-xs pt-1'>{errors.partner}</span>
          )}
        </div>

        {catalogueAvailable && (
          <div className='flex flex-col w-full'>
            <span className='pb-2 text-N-700'>
              Process<span className='text-R-500 ml-[2px]'>*</span>
            </span>
            <SimpleSelect
              isLoading={catalogueLoading}
              placeholder={
                form.partner
                  ? 'Select the process the system sends…'
                  : 'Select a partner first, or pick from every process…'
              }
              value={selectedProcessOption}
              onChange={handleProcessChange}
              options={processSelectOptions}
              isError={!!errors.process}
              restProps={{ formatOptionLabel: formatProcessOptionLabel }}
            />
            {errors.process && (
              <span className='text-R-500 text-xs pt-1'>{errors.process}</span>
            )}
            {!isOther &&
              selectedCatalogueProcess &&
              !selectedCatalogueProcess.hasOutcomes && (
                <span className='text-xs text-N-400 pt-1'>
                  Service name:{' '}
                  <span className='font-mono text-N-600'>
                    {selectedCatalogueProcess.process}
                  </span>
                </span>
              )}
          </div>
        )}

        {!catalogueAvailable && !catalogueLoading && (
          <div className='text-[12.5px] text-N-600 bg-N-50 border border-N-200 rounded-md px-3 py-2'>
            The process list is unavailable right now — enter the service name
            manually.
          </div>
        )}

        {showOutcome && (
          <div className='flex flex-col w-full'>
            <span className='pb-2 text-N-700'>
              Outcome<span className='text-R-500 ml-[2px]'>*</span>
            </span>
            <div
              role='radiogroup'
              aria-label='Outcome'
              className={CN(
                'inline-flex w-fit rounded-md border overflow-hidden',
                errors.outcome ? 'border-R-300' : 'border-N-200',
              )}>
              {OUTCOME_CHOICES.map((choice) => (
                <button
                  key={choice.value}
                  type='button'
                  role='radio'
                  aria-checked={form.outcome === choice.value}
                  onClick={() => handleOutcomeChange(choice.value)}
                  className={CN(
                    'px-4 py-[7px] text-[13px] font-semibold border-r border-N-200 last:border-r-0 outline-none focus-visible:ring-2 focus-visible:ring-P-300',
                    form.outcome === choice.value
                      ? 'bg-P-500 text-white'
                      : 'bg-white text-N-700 hover:bg-N-50',
                  )}>
                  {choice.label}
                </button>
              ))}
            </div>
            {errors.outcome && (
              <span className='text-R-500 text-xs pt-1'>{errors.outcome}</span>
            )}
            {serviceNamesPreview.length > 0 && (
              <span className='text-xs text-N-400 pt-1'>
                {serviceNamesPreview.length === 2
                  ? 'Creates two groups: '
                  : 'Service name: '}
                <span className='font-mono text-N-600'>
                  {serviceNamesPreview.join(' and ')}
                </span>
              </span>
            )}
          </div>
        )}

        {isOther && (
          <div className='flex flex-col w-full'>
            <Input
              labelValue='Service Name'
              isRequired
              type='text'
              value={form.serviceName}
              onChange={(e: any) => {
                setConflict(null)
                clearFieldError('serviceName')
                setForm((prev) => ({ ...prev, serviceName: e.target.value }))
              }}
              isError={!!errors.serviceName}
            />
            {errors.serviceName && (
              <span className='text-R-500 text-xs pt-1'>
                {errors.serviceName}
              </span>
            )}
            <div className='mt-2 text-[12.5px] text-P-800 bg-P-50 border border-P-200 rounded-md px-3 py-2 flex items-start gap-2'>
              <i className='ri-error-warning-line text-[15px] leading-5'></i>
              <span>
                The service name must <b>exactly</b> match the name the system
                sends to (spelling and case). If it does not match, these emails
                are silently never sent.
              </span>
            </div>
          </div>
        )}

        <div className='flex flex-col w-full'>
          <span className='pb-2 text-N-700'>
            Process / Service Description
            <span className='text-R-500 ml-[2px]'>*</span>
          </span>
          <input
            list='email-alert-process-options'
            value={form.serviceNameDescription}
            onChange={(e) => {
              clearFieldError('serviceNameDescription')
              setForm((prev) => ({
                ...prev,
                serviceNameDescription: e.target.value,
              }))
            }}
            className={CN(
              'w-full outline-none border border-N-200 rounded-md text-sm py-[9px] px-4',
              { 'border-R-200': !!errors.serviceNameDescription },
            )}
          />
          <datalist id='email-alert-process-options'>
            {descriptionOptions.map((option) => (
              <option key={option} value={option} />
            ))}
          </datalist>
          {errors.serviceNameDescription && (
            <span className='text-R-500 text-xs pt-1'>
              {errors.serviceNameDescription}
            </span>
          )}
          {showOutcome &&
            form.outcome === 'Both' &&
            form.serviceNameDescription.trim() && (
              <span className='text-xs text-N-400 pt-1'>
                Saved as &quot;
                {describeOutcome(
                  form.serviceNameDescription.trim(),
                  OUTCOME_SUCCESS,
                )}
                &quot; and &quot;
                {describeOutcome(
                  form.serviceNameDescription.trim(),
                  OUTCOME_FAILURE,
                )}
                &quot;
              </span>
            )}
        </div>

        <div className='flex flex-col w-full'>
          <span className='pb-2 text-N-700'>
            Tag<span className='text-R-500 ml-[2px]'>*</span>
          </span>
          <input
            list='email-alert-tag-options'
            placeholder='e.g. Inbound, Outbound'
            value={form.tag}
            onChange={(e) => {
              clearFieldError('tag')
              setForm((prev) => ({ ...prev, tag: e.target.value }))
            }}
            className={CN(
              'w-full outline-none border border-N-200 rounded-md text-sm py-[9px] px-4',
              { 'border-R-200': !!errors.tag },
            )}
          />
          <datalist id='email-alert-tag-options'>
            {tagOptions.map((option) => (
              <option key={option} value={option} />
            ))}
          </datalist>
          {errors.tag && (
            <span className='text-R-500 text-xs pt-1'>{errors.tag}</span>
          )}
        </div>

        <div className='flex flex-col w-full gap-2'>
          <span className='text-N-700'>
            Recipient email(s)
            <span className='text-R-500 ml-[2px]'>*</span>
          </span>
          {form.recipientEmails.map((email, index) => (
            <div key={index} className='flex items-center gap-2'>
              <input
                value={email}
                onChange={(e) => {
                  clearFieldError('recipientEmails')
                  updateRecipientEmail(index, e.target.value)
                }}
                onPaste={(e) => handleRecipientPaste(index, e)}
                placeholder='name@company.com'
                className={CN(
                  'flex-1 px-3 py-[9px] border rounded-md text-sm outline-none text-N-800',
                  invalidEmailRows[index] ? 'border-R-300' : 'border-N-200',
                )}
              />
              {form.recipientEmails.length > 1 && (
                <button
                  type='button'
                  title='Remove row'
                  onClick={() => removeRecipientRow(index)}
                  className='w-8 h-8 flex items-center justify-center bg-N-100 hover:bg-N-200 rounded-md text-N-600'>
                  <i className='ri-close-line text-[13px]'></i>
                </button>
              )}
            </div>
          ))}
          <button
            type='button'
            onClick={addRecipientRow}
            className='self-start text-P-600 text-[13px] font-semibold'>
            + Add another recipient
          </button>
          {errors.recipientEmails && (
            <span className='text-R-500 text-xs'>{errors.recipientEmails}</span>
          )}
          <span className='text-xs text-N-400'>
            Paste a list — addresses separated by commas, semicolons, spaces or
            new lines become one row each.
          </span>
        </div>

        {conflict && (
          <div className='text-[13px] text-P-800 bg-P-50 border border-P-200 rounded-md px-3 py-2 flex items-center gap-2 flex-wrap'>
            <span>
              <b>{form.partner?.label}</b> already has{' '}
              <b className='font-mono'>{conflict.serviceName}</b> — Manage
              recipients instead.
            </span>
            {onManageExisting && (
              <Button
                appearance='outline'
                size='xs'
                onClick={() => onManageExisting(conflict)}>
                <i className='ri-list-check pr-1'></i>
                Manage recipients
              </Button>
            )}
          </div>
        )}
      </div>

      <div className='drawer-footer flex items-center w-full h-[67px] sticky bottom-0 px-5 border-t border-N-200 shadow-sm gap-x-3'>
        <Button
          isLoading={saving === 'save'}
          disabled={isCreating}
          isFullWidth
          onClick={() => {
            void handleSave(false)
          }}>
          Save
        </Button>

        <Button
          isLoading={saving === 'another'}
          disabled={isCreating}
          isFullWidth
          appearance='outline'
          onClick={() => {
            void handleSave(true)
          }}>
          Save and add another
        </Button>

        <Button appearance='dull' onClick={resetAndClose}>
          Close
        </Button>
      </div>
    </Drawer>
  )
}

AddEmailAlertGroup.defaultProps = {
  className: undefined,
  prefill: null,
  onManageExisting: undefined,
}

export default AddEmailAlertGroup
